import { useState } from "react";
import { ERROR_MESSAGES } from "../../constant/constant.js";
import { useAuthStudent } from "../../components/context/StudentLoginContext/StudentAuthContext.jsx";
import { useAuth } from "../../components/context/LoginContext/AuthContext.jsx";

/**
 * @typedef {Object} SwitchToParentUser
 * @property {number} id
 * @property {number|null} tenant_id
 * @property {number} inst_id
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} email
 * @property {string} locale
 * @property {number} role_id
 * @property {string} reg_type
 * @property {string} status
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string|null} last_login_at
 * @property {number} role
 * @property {string} role_slug
 * @property {boolean} is_subscribed
 */

/**
 * @typedef {Object} SwitchToParentResponse
 * @property {string} token
 * @property {string} refresh_token
 * @property {number} access_token_expiry
 * @property {SwitchToParentUser} user
 */

export const useSwitchToParent = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /** @type {[SwitchToParentResponse|null, Function]} */
  const [data, setData] = useState(null);

  const { login } = useAuth();
  const { fetchWithAuthStudent, setStudent } = useAuthStudent();

  const apiUrl = process.env.REACT_APP_API_URL;

  /**
   * @param {Object} [options]
   * @param {boolean} [options.loginAsParent=false] - If true, clears student session
   *   and stores the parent data via useAuth's login() so the user is fully
   *   logged in as the parent.
   */
  const switchToParent = async ({ loginAsParent = false } = {}) => {
    try {
      setLoading(true);
      setError("");
      setData(null);

      // Get student safely
      const studentString = localStorage.getItem("student");

      if (!studentString) {
        throw new Error("Student data not found");
      }

      const student = JSON.parse(studentString);

      if (!student?.student?.createdBy) {
        throw new Error("Parent ID not found");
      }

      const parentId = student?.student?.createdBy;

      const response = await fetchWithAuthStudent(
        `${apiUrl}/student/parent/switch`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            parent_id: parentId,
          }),
        },
      );

      // Handle API errors
      if (!response.ok) {
        let errorMsg =
          ERROR_MESSAGES?.UNKNOWN || "Failed to switch to parent account";

        try {
          const errorData = await response.json();

          if (errorData?.errors) {
            const errorKey = Object.keys(errorData.errors)[0];
            errorMsg = errorData.errors[errorKey][0];
          } else if (errorData?.message) {
            errorMsg = errorData.message;
          }
        } catch {
          if (response.status === 500) {
            errorMsg =
              ERROR_MESSAGES?.INTERNAL_SERVER || "Internal server error";
          }
        }

        throw new Error(errorMsg);
      }

      /** @type {SwitchToParentResponse} */
      const responseData = await response.json();

      setData(responseData);

      localStorage.setItem("parentLocalData", JSON.stringify(responseData));

      // If loginAsParent flag is set, persist as the active parent session
      if (loginAsParent) {
        // Clear student session
        localStorage.removeItem("student");
        setStudent(null);

        // Map user.is_subscribed → user.subscription_data.is_subscribed
        // to match the GoogleLoginResponse format used everywhere else
        const mappedUser = {
          ...responseData.user,
          subscription_data: {
            is_subscribed: responseData.user?.is_subscribed ?? false,
          },
        };

        login({
          ...responseData,
          user: mappedUser,
        });
      }

      return responseData;
    } catch (err) {
      const errorMessage =
        err?.message || ERROR_MESSAGES?.UNKNOWN || "Something went wrong";

      setError(errorMessage);

      // rethrow to catch in outer try
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    switchToParent,
    loading,
    error,
    data,
    setError,
  };
};
